import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface PriceEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentPrice: string | number;
    onSave: (newPrice: string) => void;
    sessionTitle?: string;
}

const PriceEditModal: React.FC<PriceEditModalProps> = ({
    visible,
    onClose,
    currentPrice,
    onSave,
    sessionTitle = "Edit Price"
}) => {
    const [price, setPrice] = useState(currentPrice?.toString() || '');
    const [error, setError] = useState('');

    // Update state when modal opens or currentPrice changes
    useEffect(() => {
        if (visible) {
            setPrice(currentPrice?.toString() || '');
            setError('');
        }
    }, [visible, currentPrice]);

    const validatePrice = (value: string) => {
        if (!value.trim()) {
            return 'Price is required';
        }
        
        // Check if the entire string is a valid number (not just partially parseable)
        const trimmedValue = value.trim();
        const num = parseFloat(trimmedValue);
        
        // Check if the string contains only valid number characters
        // This regex allows: optional negative sign, digits, optional decimal point, optional more digits
        const isValidNumberFormat = /^-?\d+(\.\d+)?$/.test(trimmedValue);
        
        if (isNaN(num) || !isValidNumberFormat) {
            return 'Price must be a valid number';
        }
        if (num < 0) {
            return 'Price cannot be negative';
        }
        if (num > 10000) {
            return 'Price cannot exceed $10,000';
        }
        return '';
    };

    const handlePriceChange = (text: string) => {
        // Allow any characters to be typed
        setPrice(text);
        // Validate and set error immediately
        setError(validatePrice(text));
    };

    const isValid = !error && price.trim() !== '';

    const handleSave = () => {
        const validationError = validatePrice(price);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSave(price);
        onClose();
    };

    const handleCancel = () => {
        setPrice(currentPrice?.toString() || '');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>Session Price</Text>
                        <View style={[styles.inputContainer, error ? styles.inputContainerError : null]}>
                            <Text style={styles.dollarSign}>$</Text>
                            <TextInput
                                style={styles.input}
                                value={price}
                                onChangeText={handlePriceChange}
                                placeholder="0.00"
                                autoFocus={true}
                                selectTextOnFocus={true}
                            />
                        </View>
                        
                        {error ? (
                            <Text style={styles.errorText}>{error}</Text>
                        ) : (
                            <Text style={styles.helperText}>
                                Enter the price per participant for this session
                            </Text>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable 
                            onPress={handleSave} 
                            style={[styles.button, isValid ? styles.saveButton : styles.saveButtonDisabled]}
                            disabled={!isValid}
                        >
                            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 400,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: '#e9ecef',
        borderRadius: 8,
        backgroundColor: '#f8f9fa',
        paddingHorizontal: 12,
    },
    inputContainerError: {
        borderColor: '#dc3545',
        backgroundColor: '#fff5f5',
    },
    dollarSign: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
        marginRight: 4,
    },
    input: {
        flex: 1,
        padding: 12,
        fontSize: 16,
        textAlign: 'left',
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 8,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 8,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonDisabled: {
        backgroundColor: '#cccccc',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
    saveButtonTextDisabled: {
        color: '#666666',
    },
});

export default PriceEditModal; 